import Model, { hasMany, attr, belongsTo } from '@ember-data/model'
import { Type } from '@warp-drive/core-types/symbols'
import { isPresent } from '@ember/utils'
import { tracked } from '@glimmer/tracking'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import type Teacher from 'teacher-ui-client/models/teacher'
import { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import { uniqBy } from 'lodash'
import type School from 'teacher-ui-client/models/school'
import type StringTransform from 'teacher-ui-client/transforms/string'
import type NumberTransform from 'teacher-ui-client/transforms/number'

function uniqTeachersFromSchoolClasses(schoolClasses: SchoolClass[]): Teacher[] {
  return uniqBy(
    schoolClasses.reduce<Teacher[]>((acc, schoolClass) => {
      const teachers = schoolClass.teachers
      if (!teachers) return acc
      return [...acc, ...teachers]
    }, []),
    (teacher) => teacher.id,
  )
}

export class Student extends Model {
  @attr<StringTransform>('string') declare externalId: string

  @attr<StringTransform>('string') declare firstName: string

  @attr<StringTransform>('string') declare lastName: string

  @attr<NumberTransform>('number') declare gradePosition: number

  @attr<StringTransform>('string') declare login: string

  @attr<StringTransform>('string') declare plainPassword: string

  @belongsTo<School>('school', { async: false, inverse: 'students' }) declare school?: School

  @hasMany<SchoolClass>('school-class', { async: false, inverse: 'students' }) declare schoolClasses: SchoolClass[]

  get fullName() {
    return `${this.firstName} ${this.lastName}`
  }

  get fullNameLastInitial() {
    return `${this.firstName} ${this.lastName[0]}.`
  }

  get readingSchoolClasses() {
    return this.schoolClassesFor(SubscriptionType.Reading)
  }

  get mathsSchoolClasses() {
    return this.schoolClassesFor(SubscriptionType.Maths)
  }

  get writingSchoolClasses() {
    return this.schoolClassesFor(SubscriptionType.Writing)
  }

  schoolClassesFor(subscriptionType: SubscriptionType) {
    return this.schoolClasses.filter((item) => item.subscriptionType === subscriptionType)
  }

  get wordFlyersSchoolClasses() {
    return this.schoolClasses.filter((item) => (item.subscriptionType as string) === 'wf')
  }

  get readingSchoolClassNames() {
    return this.schoolClassesFor(SubscriptionType.Reading).map((item) => item.name)
  }

  get mathsSchoolClassNames() {
    return this.schoolClassesFor(SubscriptionType.Maths).map((item) => item.name)
  }

  get writingSchoolClassNames() {
    return this.schoolClassesFor(SubscriptionType.Writing).map((item) => item.name)
  }

  get readingSchoolClassNamesCommaSeparated() {
    return this.readingSchoolClassNames.join(', ')
  }

  get mathsSchoolClassNamesCommaSeparated() {
    return this.mathsSchoolClassNames.join(', ')
  }

  get writingSchoolClassNamesCommaSeparated() {
    return this.writingSchoolClassNames.join(', ')
  }

  get readingSchoolClassAllocated() {
    return this.isAllocatedForSubscriptionType(SubscriptionType.Reading)
  }

  get mathsSchoolClassAllocated() {
    return this.isAllocatedForSubscriptionType(SubscriptionType.Maths)
  }

  get writingSchoolClassAllocated() {
    return this.isAllocatedForSubscriptionType(SubscriptionType.Writing)
  }

  isAllocatedForSubscriptionType(subscriptionType: SubscriptionType) {
    return isPresent(this.schoolClassesFor(subscriptionType))
  }

  /**
   * When displaying a students' teachers in the UI, it is highly likely you want to only show teachers for classes of
   * the student for a single subscription-type. Consider using teachersFor(subscriptionType)
   */
  get allTeachers() {
    return uniqTeachersFromSchoolClasses(this.schoolClasses)
  }

  teachersFor(subscriptionType: SubscriptionType) {
    return uniqTeachersFromSchoolClasses(this.schoolClassesFor(subscriptionType))
  }

  get readingTeachers() {
    return this.teachersFor(SubscriptionType.Reading)
  }

  get mathsTeachers() {
    return this.teachersFor(SubscriptionType.Maths)
  }

  get writingTeachers() {
    return this.teachersFor(SubscriptionType.Maths)
  }

  get readingTeacherNames() {
    return this.teachersFor(SubscriptionType.Reading).map((item) => item.fullName)
  }

  get mathsTeacherNames() {
    return this.teachersFor(SubscriptionType.Maths).map((item) => item.fullName)
  }

  get writingTeacherNames() {
    return this.teachersFor(SubscriptionType.Writing).map((item) => item.fullName)
  }

  @tracked private savedRelationships?: { schoolClasses?: SchoolClass[] }

  saveRelationships() {
    const savedRelationships = {
      schoolClasses: this.schoolClasses.slice(),
    }
    this.savedRelationships = savedRelationships
  }

  rollbackRelationships() {
    const schoolClasses = this.savedRelationships?.schoolClasses

    if (!schoolClasses) return

    this.schoolClasses = schoolClasses
  }

  [Type] = 'student' as const
}

export default Student
